/* eslint-disable no-alert */
/* eslint-disable prefer-template */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button, Chip, Typography, Box } from "@material-ui/core";
import { People, Person } from "@material-ui/icons";
import DealersDataGrid from "./data-grid/DealersDataGrid";
import { useAuth } from "../../contexts/AuthContext";
import { useSB } from "../../contexts/SupabaseContext";
import { useCompany } from "../../contexts/CompanyContext";
import getStatusName from "../shared/functions/getStatusName";
import * as API from "../../api";

/**
 * Enhanced table component for displaying and searching dealers with improved performance.
 * Includes search functionality across multiple fields and lifecycle status filtering.
 *
 * @component
 * @returns Enhanced table component with search, filter, and bulk update capabilities
 */
export default function DealersTable() {
  const [allDealers, setAllDealers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    totalCount: 0,
    filteredCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
    startRecord: 0,
    endRecord: 0,
  });
  const [queryInfo, setQueryInfo] = useState({
    search: "",
    statusFilter: 0,
    executionTime: null,
  });
  const { updateRow } = useSB();
  const { lifecycleStatuses } = useCompany();
  const { currentUser, authSession } = useAuth();

  const [state, setState] = useState({
    loading: false,
    error: null,
    dataLoaded: false,
  });

  const [selectedRows, setSelectedRows] = useState([]);

  // Fetch dealers data with pagination and filters
  const fetchDealers = useCallback(
    async (page = 1, limit = 50, search = "", statusFilter = 0) => {
      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        const params = {
          dealer_id: currentUser.id,
          access_token: authSession.access_token,
          page,
          limit,
        };

        // Only add search and statusFilter if they have values
        if (search && search.trim()) {
          params.search = search.trim();
        }
        if (statusFilter && statusFilter !== 0) {
          params.status_filter = statusFilter;
        }

        const res = await API.getDealersForAdmin(params);

        if (res.data.code !== 200) {
          throw new Error(res.data.message || "Failed to fetch dealers");
        }

        setAllDealers(res.data.data || []);
        setPagination(res.data.pagination || {});
        setQueryInfo(res.data.query || {});
        setState(prev => ({ ...prev, loading: false, dataLoaded: true }));
      } catch (err) {
        console.error("Error fetching dealers:", err);
        setState(prev => ({
          ...prev,
          loading: false,
          error: err.message,
        }));
        alert(err.message);
      }
    },
    [currentUser.id, authSession.access_token]
  );

  // Initial data fetch
  useEffect(() => {
    fetchDealers();
  }, [fetchDealers]);

  // Transform raw dealer data to rows format for the grid
  const rows = useMemo(() => {
    if (!allDealers.length) return [];

    const transformedRows = allDealers.map(dealer => ({
      ...dealer,
      id: dealer.id,
      name: `${dealer.first_name} ${dealer.surname}`,
      phone: dealer.phone ? "0" + dealer.phone.slice(3) : "",
      parentDealer: dealer.parent_id
        ? `${dealer.parent_first_name} ${dealer.parent_surname}`
        : "",
    }));

    return transformedRows;
  }, [allDealers]);

  // Selection handler with performance optimization
  const handleSelect = useCallback(selection => {
    if (selection?.length >= 500) {
      window.alert("Maximum of 500 rows selectable.");
    } else {
      setSelectedRows(selection);
    }
  }, []);

  // Pagination handlers
  const handlePageChange = useCallback(
    (newPage, newLimit) => {
      fetchDealers(newPage, newLimit, queryInfo.search, queryInfo.statusFilter);
    },
    [fetchDealers, queryInfo.search, queryInfo.statusFilter]
  );

  // Search and filter handlers
  const handleSearch = useCallback(
    (searchTerm, statusFilter) => {
      // Reset to page 1 when searching
      fetchDealers(1, pagination.limit, searchTerm, statusFilter);
    },
    [fetchDealers, pagination.limit]
  );

  /**
   * Performs a bulk update on selected dealers
   * @param {Array<object>} dealerArray - Array of dealer objects to update
   * @param {object} data - Data to update each dealer with
   */
  const bulkUpdateDealers = useCallback(
    async (dealerArray, data) => {
      try {
        await Promise.all(
          dealerArray.map(async d => {
            const updateData = {
              ...data,
              last_updated_by: currentUser.auth_id,
            };
            await updateRow("users_master", d.id, updateData);
          })
        );
      } catch (error) {
        console.error("Error updating users_master:", error);
        throw error;
      }
    },
    [updateRow, currentUser.auth_id]
  );

  const handleBulkUpdate = useCallback(
    async data => {
      setState(prev => ({ ...prev, loading: true, error: null }));
      try {
        await bulkUpdateDealers(selectedRows, data);

        // Refresh current page after bulk update
        await fetchDealers(
          pagination.page,
          pagination.limit,
          queryInfo.search,
          queryInfo.statusFilter
        );

        setSelectedRows([]); // Clear selection after update
        setState(prev => ({ ...prev, loading: false }));
      } catch (err) {
        setState(prev => ({
          ...prev,
          loading: false,
          error: err.message,
        }));
      }
    },
    [
      selectedRows,
      bulkUpdateDealers,
      fetchDealers,
      pagination.page,
      pagination.limit,
      queryInfo.search,
      queryInfo.statusFilter,
    ]
  );

  // Table columns configuration
  const columns = [
    {
      field: "id",
      headerName: "Code",
      width: 100,
      renderCell: params => (
        <div className="d-flex justify-content-between align-items-center">
          {`Z-${params.row.id}`}
        </div>
      ),
    },
    {
      field: "first_name",
      headerName: "Name",
      width: 210,
      renderCell: params => (
        <div className="d-flex justify-content-between align-items-center">
          {`${params.row.first_name} ${params.row.surname}`}
        </div>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      sortable: false,
      width: 140,
      renderCell: params => (
        <div className="d-flex justify-content-between align-items-center">
          <Chip
            color={
              params.row.refs_user_lifecycle_status_id === 2
                ? "primary"
                : "secondary"
            }
            label={getStatusName(
              parseInt(params.row.refs_user_lifecycle_status_id),
              lifecycleStatuses
            )}
            size="small"
          />
        </div>
      ),
    },
    { field: "phone", headerName: "Phone", width: 120 },
    { field: "email", headerName: "Email", width: 210 },
    {
      headerName: "Parent",
      width: 180,
      renderCell: params => (
        <div className="d-flex justify-content-between align-items-center">
          {params.row.parent_id
            ? `Z-${params.row.parent_id}: ${params.row.parent_first_name} ${params.row.parent_surname}`
            : "Top Level"}
        </div>
      ),
    },
    {
      field: "details",
      headerName: "Details",
      sortable: false,
      width: 120,
      renderCell: params => (
        <div className="d-flex justify-content-between align-items-center">
          <Link to={`/user-detail/${params.row.id}`}>
            <Button startIcon={<Person style={{ opacity: 0.5 }} />}>
              Details
            </Button>
          </Link>
        </div>
      ),
    },
    {
      field: "downstream",
      headerName: "Downstream",
      sortable: false,
      width: 140,
      // eslint-disable-next-line no-confusing-arrow
      renderCell: params =>
        !params.row.is_leader && !params.row.is_admin ? null : (
          <div className="d-flex justify-content-between align-items-center">
            <Link to={`/child-dealer-downstream/${params.row.id}`}>
              <Button startIcon={<People style={{ opacity: 0.5 }} />}>
                Downstream
              </Button>
            </Link>
          </div>
        ),
    },
    {
      field: "email_verified",
      headerName: "Email Verified",
      sortable: true,
      width: 140,
      renderCell: params => (
        <div
          style={{
            textTransform: "capitalize",
            justifyContent: "center",
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Chip
            color={params.row.email_verified ? "primary" : "secondary"}
            label={params.row.email_verified ? "Yes" : "No"}
            size="small"
          />
        </div>
      ),
    },
    {
      field: "phone_verified",
      headerName: "Phone Verified",
      sortable: true,
      width: 140,
      renderCell: params => (
        <div
          style={{
            textTransform: "capitalize",
            justifyContent: "center",
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Chip
            color={params.row.phone_verified ? "primary" : "secondary"}
            label={params.row.phone_verified ? "Yes" : "No"}
            size="small"
          />
        </div>
      ),
    },
  ];

  if (state.error) {
    return (
      <Box p={2}>
        <Typography color="error">
          Error loading dealers: {state.error}
        </Typography>
      </Box>
    );
  }

  return (
    <div>
      <DealersDataGrid
        rowsData={rows}
        columns={columns}
        loading={state.loading || !state.dataLoaded}
        handleSelect={handleSelect}
        handleUpdate={handleBulkUpdate}
        updateRunning={state.loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        queryInfo={queryInfo}
      />
    </div>
  );
}
